/**
 * Frontend-only row lane layout for overlapping Schedule scope blocks.
 *
 * Blocks remain attached to their original project/group row; this helper only
 * assigns visual lanes and row heights so overlapping date spans stack vertically.
 */
import { toLocalDateKey } from "../../../../utils/datetime/core";
import { getScheduleBlockRowId } from "./scheduleRowModel";

/**
 * Inclusive date span for overlap detection. The persisted block span is authoritative;
 * aspect rows may be trimmed to the currently loaded schedule data window.
 *
 * @param {Object} block
 * @param {Array<Object>} workScopeAspects
 * @returns {{ startKey: string, endKey: string }} `YYYY-MM-DD` keys.
 */
function getBlockSpan(block, workScopeAspects) {
  return {
    startKey: toLocalDateKey(block.startDate),
    endKey: toLocalDateKey(block.endDate),
  };
}

/**
 * Assign each visible block to the first non-overlapping visual lane in its row.
 *
 * @param {Object} params
 * @param {Array<Object>} params.scheduleRows
 * @param {Array<Object>} params.blocks
 * @param {Array<Object>} params.workScopeAspects
 * @param {Array<Date>} params.visibleDays
 * @param {number} params.baseRowHeight
 * @returns {{
 *   rowHeightsById: Map<string, number>,
 *   rowTopById: Map<string, number>,
 *   laneByBlockId: Map<string, number>,
 *   laneCountByRowId: Map<string, number>,
 *   totalGridHeight: number,
 * }}
 */
export function buildScheduleOverlapLayout({
  scheduleRows = [],
  blocks = [],
  workScopeAspects = [],
  visibleDays = [],
  baseRowHeight,
}) {
  const rowOrderById = new Map(scheduleRows.map((row, index) => [row.id, index]));
  const visibleDayKeys = visibleDays.map((day) => toLocalDateKey(day));
  const candidates = [];

  for (const block of blocks ?? []) {
    const rowId = getScheduleBlockRowId(block);
    if (!rowOrderById.has(rowId)) {
      continue;
    }
    const span = getBlockSpan(block, workScopeAspects);
    const hasVisibleDay = visibleDayKeys.some(
      (dayKey) => span.startKey <= dayKey && dayKey <= span.endKey
    );
    if (!hasVisibleDay) {
      continue;
    }
    candidates.push({
      blockId: String(block.id),
      rowId,
      rowOrder: rowOrderById.get(rowId),
      startKey: span.startKey,
      endKey: span.endKey,
    });
  }

  candidates.sort((a, b) => {
    if (a.rowOrder !== b.rowOrder) return a.rowOrder - b.rowOrder;
    if (a.startKey !== b.startKey) return a.startKey.localeCompare(b.startKey);
    if (a.endKey !== b.endKey) return a.endKey.localeCompare(b.endKey);
    return a.blockId.localeCompare(b.blockId);
  });

  const laneEndsByRowId = new Map();
  const laneByBlockId = new Map();
  const laneCountByRowId = new Map();

  // First-fit: reuse a lane only when the previous occupant ended before this start
  // (inclusive spans, so end === start still stacks). New lanes grow the row height.
  for (const block of candidates) {
    const laneEnds = laneEndsByRowId.get(block.rowId) ?? [];
    let lane = laneEnds.findIndex((endKey) => endKey < block.startKey);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(block.endKey);
    } else {
      laneEnds[lane] = block.endKey;
    }
    laneEndsByRowId.set(block.rowId, laneEnds);
    laneByBlockId.set(block.blockId, lane);
    laneCountByRowId.set(block.rowId, laneEnds.length);
  }

  const rowHeightsById = new Map();
  const rowTopById = new Map();
  let top = 0;
  for (const row of scheduleRows) {
    const laneCount = Math.max(1, laneCountByRowId.get(row.id) ?? 1);
    const height = laneCount * baseRowHeight;
    rowTopById.set(row.id, top);
    rowHeightsById.set(row.id, height);
    top += height;
  }

  return {
    rowHeightsById,
    rowTopById,
    laneByBlockId,
    laneCountByRowId,
    totalGridHeight: top,
  };
}
